import { persistedState } from 'svelte-persisted-state';

import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';

import { DEFAULT_ROASTER_SETTINGS, DeviceType, DeviceTypeLabel } from '$lib/constants';
import {
  DEFAULT_RTD_TYPE,
  DEFAULT_RTD_WIRE_SETUP,
  type PhidgetsRTDSettings
} from '$lib/devices/phidgets/rtdConfig';
import { getDeviceConfig, getDeviceLabels } from '$lib/devices/registry';
import logger from '$lib/logger';
import { roastingEvents } from '$lib/RoastingEvents.svelte';
import { RoastingQueue } from '$lib/RoastQueue.svelte';
import type {
  Beans,
  JWTPayload,
  Locations,
  Nullable,
  ResourceVersions,
  Roastables,
  RoastChartData,
  RoasterMapping,
  Roasts,
  StarredRoast,
  Thermocouple,
  TimeTempPair,
  Token,
  UserProfile
} from '$lib/types/RoastLog';
import { MassScale } from '$lib/types/RoastLog';
import { streamingState } from './liveStream/streamingState.svelte';
import { floatToDecimal } from './RLogUtils';

const TOKEN_KEY = 't';

class Session {
  #token = persistedState<Nullable<string>>(TOKEN_KEY, null);

  constructor() {
    if (this.#token.current && !this.isExpired()) {
      this.setDatadogContext(this.#token.current);
    }
  }

  reset = () => {
    this.#token.reset();
  };

  isExpired = (): boolean => {
    if (!this.#token.current) {
      return true;
    }

    const expiry = this.parseJWT(this.#token.current).exp;
    return Math.floor(new Date().getTime() / 1000) >= expiry;
  };

  get daysToExpire(): number {
    if (!this.#token.current) {
      return 0;
    }
    const jwtPayload = this.parseJWT(this.#token.current);
    return this.getDaysToTokenExpire(jwtPayload);
  }

  get hasToken(): boolean {
    return this.#token !== null;
  }

  get token(): Token {
    return this.#token.current;
  }

  set token(t: Token) {
    this.#token.current = t;
    this.setDatadogContext(t);
  }

  private setDatadogContext = (token: Token) => {
    const jwtPayload = this.parseJWT(token);
    const userContext = {
      ...jwtPayload,
      id: jwtPayload.user_id.toString(),
      daysToExpire: this.getDaysToTokenExpire(jwtPayload).toFixed(1)
    };

    datadogLogs.setUser(userContext);
    datadogRum.setUser({
      id: jwtPayload.user_id.toString(),
      email: jwtPayload.email
    });
  };

  private parseJWT = (token: Token): JWTPayload => {
    if (token) {
      return JSON.parse(atob(token.split('.')[1]));
    }
    return { user_id: -1, username: '', exp: 0, email: '' };
  };

  private getDaysToTokenExpire = (jwtPayload: JWTPayload): number => {
    return floatToDecimal((jwtPayload.exp - Math.floor(new Date().getTime() / 1000)) / 86400);
  };
}
export const session = new Session();

// end token

export const roastQueue = new RoastingQueue();
export const isNewLogin = persistedState('NL', false);
export const isDemo = persistedState('IDRL', false);
export const plotBurner = persistedState('plotBurner', true);

export const locations = persistedState<Locations>('locations', []);
export const selectedLocation = persistedState<Nullable<string>>('selectedLocation2', null);

export const roasters = persistedState<RoasterMapping>('roasters', {} as RoasterMapping);
export const beans = persistedState<Beans>('beans', [] as Beans);
export const roasts = persistedState<Roasts>('roasts', [] as Roasts);
export const roastables = persistedState<Roastables>('roastables', [] as Roastables);

export const startStopShortcutEnabled = persistedState<boolean>('startStopShortcutEnabled', false);
export const startStopShortcutKey = persistedState<string>('startStopShortcutKey', 'space');

export const referenceRoastEventDisplay = persistedState<'dots' | 'labels'>(
  'referenceRoastEventDisplay',
  'dots'
);

export const resourceVersions = persistedState<ResourceVersions>('versions', {
  beans: -1,
  roasts: -1,
  roastables: -1,
  roasters: -1
});
export const lastSavedRoastChart = persistedState<Nullable<RoastChartData>>(
  'lastSavedRoastChart',
  null
);
export const lastSavedBurnerChart = persistedState<Nullable<TimeTempPair[]>>(
  'lastSavedBurnerChart',
  null
);

export const getBeanNameById = (beanId: number): string => {
  const bean = beans.current.find((b) => b.id == beanId);
  if (bean) {
    return bean.name;
  }
  return 'Unknown';
};

class SelectedDevice {
  #selectedDeviceType = persistedState('selectedDeviceType', {
    label: 'Phidgets',
    value: DeviceType.PHIDGETS
  });

  get config() {
    return getDeviceConfig(this.#selectedDeviceType.current.value);
  }

  get isFake(): boolean {
    return this.#selectedDeviceType.current.value === DeviceType.FAKE;
  }

  get isLoring(): boolean {
    return this.#selectedDeviceType.current.value === DeviceType.LORING;
  }

  get isSivetz(): boolean {
    return this.#selectedDeviceType.current.value === DeviceType.SIVETZ;
  }

  get isSanFranciscan(): boolean {
    return this.#selectedDeviceType.current.value === DeviceType.SAN_FRANCISCAN;
  }

  get isSanFranciscanRTU(): boolean {
    return this.#selectedDeviceType.current.value === DeviceType.SAN_FRANCISCAN_RTU;
  }

  get hasHardcodedLabels(): boolean {
    return this.config.hasHardcodedLabels;
  }

  get hasBurnerChart(): boolean {
    return this.config.hasBurnerChart;
  }

  get hasModbusSettings(): boolean {
    return this.config.hasModbusSettings;
  }

  get sensorLabel(): string {
    return this.config.sensorLabel ?? 'TC';
  }

  get hasTcTypeToggle(): boolean {
    return this.config.hasTcTypeToggle ?? true;
  }

  get isPhidgets(): boolean {
    return this.#selectedDeviceType.current.value === DeviceType.PHIDGETS;
  }

  get isBTLE(): boolean {
    return this.#selectedDeviceType.current.value === DeviceType.RLBTLE;
  }

  get isRLUSB(): boolean {
    return this.#selectedDeviceType.current.value === DeviceType.RLUSB;
  }

  get label(): string {
    return this.config.label;
  }

  get value(): DeviceType {
    return this.#selectedDeviceType.current.value;
  }

  set value(dt: DeviceType) {
    const config = getDeviceConfig(dt);

    this.#selectedDeviceType.current = {
      label: config.label,
      value: dt
    };
  }

  getSelection(type: DeviceType) {
    const config = getDeviceConfig(type);
    return {
      label: config.label,
      value: type
    };
  }

  isType(dt: DeviceType) {
    return this.#selectedDeviceType.current.value === dt;
  }
}

export const selectedDeviceType = new SelectedDevice();

type StarredRoastMapping = { [key: number]: StarredRoast };
export const starredRoasts = persistedState<StarredRoastMapping>(
  'starredRoasts',
  {} as StarredRoastMapping
);

export const userProfile = persistedState<Nullable<UserProfile>>('userProfile', null);

export const modbusSettings = persistedState('modbusSettings', {
  host: '192.168.1.199',
  register: '767'
});

export const sivetzSettings = persistedState('sivetzSettings', {
  host: '10.0.0.100',
  register: '28672'
});

export const sanFranciscanSettings = persistedState('sanFranciscanSettings', {
  host: '192.168.1.100',
  register: '360'
});

export const sanFranciscanRTUSettings = persistedState('sanFranciscanRTUSettings', {
  portName: null as string | null,
  slaveId: 1
});

export const primoRTUSettings = persistedState('primoRTUSettings', {
  portName: null as string | null,
  slaveId: 1
});

export const coffedRTUSettings = persistedState('coffedRTUSettings', {
  portName: null as string | null,
  slaveId: 1
});

export const yoshanRTUSettings = persistedState('yoshanRTUSettings', {
  portName: null as string | null,
  slaveId: 1
});

export const coffedModbusSettings = persistedState('coffedModbusSettings', {
  host: '192.168.1.100'
});

// Device-level RTD configuration applied to all Phidgets RTD probes.
export const phidgetsRTDSettings = persistedState<PhidgetsRTDSettings>('phidgetsRTDSettings', {
  rtdType: DEFAULT_RTD_TYPE,
  rtdWireSetup: DEFAULT_RTD_WIRE_SETUP
});

class SelectedRoaster {
  #roasterId = persistedState<Nullable<string>>('selectedRoaster2', null);
  tcSettings = persistedState('tcSettings', [] as Thermocouple[]);
  // Persisted mapping of roaster ID to mass scale
  #massScaleByRoaster = persistedState<Record<string, MassScale>>('roasterMassScales', {});
  #roaster = $derived.by(() => {
    if (this.#roasterId.current) {
      return roasters.current[this.#roasterId.current];
    }
    return null;
  });

  constructor() {
    this.tcSettings.current = this.getTCSettings();
  }

  setTempOffset(tcNum: number, offset: number) {
    if (this.tcSettings.current[tcNum]) {
      this.tcSettings.current[tcNum].offset = offset;
      logger.info('Temp offset change', {
        tcNum,
        offset,
        tcSettings: this.tcSettings.current
      });
    }
  }

  reset() {
    this.#roasterId.reset();
    this.tcSettings.reset();
  }

  get name() {
    return this.#roaster?.name || null;
  }

  get roasterId(): Nullable<string> {
    return this.#roasterId.current;
  }

  set roasterId(rId: string) {
    this.#roasterId.current = rId;
    this.tcSettings.current = this.getTCSettings();
    logger.info('Selected roaster change', {
      roasterId: this.#roasterId.current,
      tcSettings: this.tcSettings.current,
      name: this.name
    });
  }

  get tcLabels() {
    const [, tc2Active, tc3Active, tc4Active] = this.tcSettings.current.map((tc) => tc.active);
    const deviceLabels = getDeviceLabels(selectedDeviceType.value);

    if (selectedDeviceType.hasHardcodedLabels && deviceLabels) {
      return {
        tc2: tc2Active ? deviceLabels.TC2 : null,
        tc3: tc3Active ? deviceLabels.TC3 : null,
        tc4: tc4Active ? deviceLabels.TC4 : null
      };
    }

    return {
      tc2: tc2Active ? this.tcSettings.current[1].name : null,
      tc3: tc3Active ? this.tcSettings.current[2].name : null,
      tc4: tc4Active ? this.tcSettings.current[3].name : null
    };
  }

  get tcLabelNames(): Array<string | null> {
    const deviceLabels = getDeviceLabels(selectedDeviceType.value);

    if (deviceLabels) {
      // Return array of label values for devices with hardcoded labels
      return [deviceLabels.TC1, deviceLabels.TC2, deviceLabels.TC3, deviceLabels.TC4];
    }

    // Return null array for devices without hardcoded labels
    return [null, null, null, null];
  }

  get massScale(): MassScale {
    if (!this.#roasterId.current) {
      return userProfile.current?.profile?.mass_scale ?? MassScale.Pounds;
    }

    // First check persisted local setting for this roaster
    const persistedScale = this.#massScaleByRoaster.current[this.#roasterId.current];
    if (persistedScale) {
      return persistedScale;
    }

    // Then check server settings
    if (this.#roaster?.settings?.mass_scale) {
      return this.#roaster.settings.mass_scale;
    }

    // Finally fall back to user profile
    return userProfile.current?.profile?.mass_scale ?? MassScale.Pounds;
  }

  set massScale(scale: MassScale) {
    if (this.#roasterId.current) {
      this.#massScaleByRoaster.current = {
        ...this.#massScaleByRoaster.current,
        [this.#roasterId.current]: scale
      };
      logger.info('Mass scale changed for roaster', {
        roasterId: this.#roasterId.current,
        massScale: scale
      });
    }
  }

  private getTCSettings() {
    if (!this.#roasterId.current) {
      return [
        DEFAULT_ROASTER_SETTINGS['1'],
        DEFAULT_ROASTER_SETTINGS['2'],
        DEFAULT_ROASTER_SETTINGS['3'],
        DEFAULT_ROASTER_SETTINGS['4']
      ];
    }

    let _selectedRoaster = roasters.current[this.#roasterId.current];

    // The roaster may be been deleted on the server. In that case, pick the first one in the list.
    if (!_selectedRoaster) {
      const roasterIds = Object.keys(roasters);
      _selectedRoaster = roasters.current[roasterIds[0]];

      if (_selectedRoaster) {
        // This is ok, the case where the roaster has been deleted on the backend, but it was
        // selected on the frontend. We just pick the first one.
        this.#roasterId.current = _selectedRoaster.id;
      } else {
        // This happens on logout from the setting page...all of the stores are removed, so the
        // roaster is undefined.
        roasters.reset();
      }
    }

    const tcSettings = _selectedRoaster?.settings?.thermocouples
      ? [
          _selectedRoaster.settings.thermocouples['1'],
          _selectedRoaster.settings.thermocouples['2'],
          _selectedRoaster.settings.thermocouples['3'],
          _selectedRoaster.settings.thermocouples['4']
        ]
      : [
          DEFAULT_ROASTER_SETTINGS['1'],
          DEFAULT_ROASTER_SETTINGS['2'],
          DEFAULT_ROASTER_SETTINGS['3'],
          DEFAULT_ROASTER_SETTINGS['4']
        ];

    return tcSettings;
  }
}

export const selectedRoaster = new SelectedRoaster();

class HeatingRateControls {
  #heatingRate = persistedState('hrControls', {
    deltaSpan: 15,
    avgWindowLength: 5
  });

  get deltaSpan() {
    return this.#heatingRate.current.deltaSpan;
  }
  set deltaSpan(value: number) {
    if (value < 2) value = 2;
    else if (value > 30) value = 30;
    this.#heatingRate.current.deltaSpan = value;
  }

  get avgWindowLength() {
    return this.#heatingRate.current.avgWindowLength;
  }

  set avgWindowLength(value: number) {
    if (value < 1) value = 1;
    else if (value > 30) value = 30;
    this.#heatingRate.current.avgWindowLength = value;
  }

  reset() {
    this.#heatingRate.reset();
  }
}
export const hrControls = new HeatingRateControls();

export const clearStateForLocationChange = () => {
  beans.reset();
  roastables.reset();
  roasters.reset();
  roasts.reset();
  selectedRoaster.reset();
  resourceVersions.reset();
};

export const clearState = () => {
  // Disconnect the live stream and reset toggle before clearing auth state
  streamingState.reset();

  const OBJECT_KEYS = [
    TOKEN_KEY,
    'NL',
    'lastSavedBurnerChart',
    'lastSavedRoastChart',
    //  Don't reset the modbus settings since these are not stored on the server
    // 'modbusSettings',
    'token' // Also remove the legacy token key
  ];
  session.reset();
  beans.reset();
  hrControls.reset();
  isNewLogin.reset();
  lastSavedRoastChart.reset();
  lastSavedBurnerChart.reset();
  locations.reset();
  // modbusSettings.reset();
  //
  resourceVersions.reset();
  roastQueue.reset();
  roastables.reset();
  roasters.reset();
  roastingEvents.clear();
  roasts.reset();
  selectedLocation.reset();
  selectedRoaster.reset();
  userProfile.reset();
  OBJECT_KEYS.forEach((key) => localStorage.removeItem(key));
};
